import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Table,
  Spinner,
  Row,
  Col,
  InputGroup,
  Badge,
} from "react-bootstrap";
import { useAuth } from "../context/AuthContext";
import { useLeague } from "../hooks/useLeague";
import { apiGet } from "../utils/apiClient";
import { getPlayersUrl } from "../config/api";

// Type Definitions
interface Player {
  id: number;
  first_name: string;
  last_name: string;
  league_id: number;
}

interface Position {
  id: number;
  name: string;
  abbr: string;
}

// Hardcoded positions - these are static reference data from the backend seeds
const POSITIONS: Position[] = [
  { id: 1, name: "Forward", abbr: "F" },
  { id: 2, name: "Defenceman", abbr: "D" },
  { id: 3, name: "Goaltender", abbr: "G" },
  { id: 4, name: "Center", abbr: "C" },
  { id: 5, name: "Right Wing", abbr: "RW" },
  { id: 6, name: "Left Wing", abbr: "LW" },
];

interface PlayerPickerModalProps {
  show: boolean;
  onClose: () => void;
  onPlayerSelected: (
    playerId: number,
    positionId: number,
    jersey: string,
  ) => void;
  teamId: number;
  existingPlayerIds: number[];
}

function PlayerPickerModal({
  show,
  onClose,
  onPlayerSelected,
  teamId,
  existingPlayerIds,
}: PlayerPickerModalProps) {
  const auth = useAuth();
  const { currentLeague } = useLeague();

  const [searchQuery, setSearchQuery] = useState("");
  const [players, setPlayers] = useState<Player[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Selection state
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<number>(0);
  const [jerseyNumber, setJerseyNumber] = useState("");

  // Load/search players with debouncing and abort signal
  useEffect(() => {
    if (!show || !currentLeague) {
      return;
    }

    const abortController = new AbortController();
    const trimmedQuery = searchQuery.trim();

    // If no search query, load immediately without debounce
    // If there's a search query, debounce it
    const delay = trimmedQuery === "" ? 0 : 250;

    const timeoutId = setTimeout(() => {
      setIsLoading(true);
      setError("");

      // Build URL with or without search query
      const baseUrl = getPlayersUrl(currentLeague.id);
      const searchUrl = trimmedQuery
        ? `${baseUrl}?q=${encodeURIComponent(
            trimmedQuery,
          )}&limit=20&sort=last_name,first_name`
        : `${baseUrl}?limit=20&sort=last_name,first_name`;

      apiGet(searchUrl, {
        token: auth.token,
        leagueAbbr: currentLeague.abbr,
      })
        .then((response) => {
          if (!abortController.signal.aborted) {
            // Handle paginated response - extract data array
            const playersData = Array.isArray(response)
              ? response
              : response.data || [];
            setPlayers(playersData);
          }
        })
        .catch((err) => {
          if (!abortController.signal.aborted) {
            console.error("Error loading players:", err);
            setError(err.message || "Failed to load players");
          }
        })
        .finally(() => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
          }
        });
    }, delay);

    return () => {
      clearTimeout(timeoutId);
      abortController.abort();
    };
  }, [show, currentLeague, searchQuery, auth.token]);

  // Display players (no pagination needed since backend limits to 20)
  const displayPlayers = players;

  // Handle player selection
  const handleSelectPlayer = (player: Player) => {
    setSelectedPlayer(player);
    setSelectedPositionId(0);
    setJerseyNumber("");
  };

  // Handle add player
  const handleAddPlayer = () => {
    if (!selectedPlayer) {
      setError("Please select a player");
      return;
    }

    onPlayerSelected(
      selectedPlayer.id,
      selectedPositionId || 0,
      jerseyNumber.trim(),
    );

    // Reset selection to allow adding another player without closing modal
    setSelectedPlayer(null);
    setSelectedPositionId(0);
    setJerseyNumber("");
    setError("");
  };

  // Handle close
  const handleClose = () => {
    setSearchQuery("");
    setPlayers([]);
    setSelectedPlayer(null);
    setSelectedPositionId(0);
    setJerseyNumber("");
    setError("");
    onClose();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Add Player to Team</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {/* Search Input */}
        <Form.Group className="mb-3">
          <Form.Label>Search Players</Form.Label>
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search by first or last name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <Button
                variant="outline-secondary"
                onClick={() => setSearchQuery("")}
              >
                Clear
              </Button>
            )}
          </InputGroup>
          <Form.Text className="text-muted">
            {isLoading
              ? "Searching..."
              : searchQuery.trim() === ""
                ? `Showing ${players.length} player(s)${
                    players.length === 20 ? " (first 20)" : ""
                  }`
                : `${players.length} player(s) found${
                    players.length === 20 ? " (showing top 20)" : ""
                  }`}
          </Form.Text>
        </Form.Group>

        {/* Players Table */}
        {isLoading ? (
          <div className="text-center p-4">
            <Spinner animation="border" /> Loading...
          </div>
        ) : (
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {displayPlayers.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center">
                    {searchQuery.trim() === ""
                      ? "No players available"
                      : `No players found matching "${searchQuery}"`}
                  </td>
                </tr>
              ) : (
                displayPlayers.map((player) => {
                  const isAlreadyOnTeam = existingPlayerIds.includes(player.id);
                  const isSelected = selectedPlayer?.id === player.id;
                  return (
                    <React.Fragment key={player.id}>
                      <tr className={isSelected ? "table-active" : ""}>
                        <td>{player.first_name}</td>
                        <td>{player.last_name}</td>
                        <td>
                          {isAlreadyOnTeam ? (
                            <Badge bg="success">On Team</Badge>
                          ) : (
                            <Button
                              size="sm"
                              variant={isSelected ? "success" : "primary"}
                              onClick={() => handleSelectPlayer(player)}
                            >
                              {isSelected ? "Selected" : "Select"}
                            </Button>
                          )}
                        </td>
                      </tr>
                      {isSelected && (
                        <tr className="table-active">
                          <td colSpan={3}>
                            <div className="p-3">
                              <h6 className="mb-3">
                                Adding: {player.first_name} {player.last_name}
                              </h6>
                              <Row>
                                <Col md={5}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>
                                      Jersey Number (Optional)
                                    </Form.Label>
                                    <Form.Control
                                      type="text"
                                      placeholder="e.g., 23"
                                      value={jerseyNumber}
                                      onChange={(e) =>
                                        setJerseyNumber(e.target.value)
                                      }
                                    />
                                  </Form.Group>
                                </Col>
                                <Col md={5}>
                                  <Form.Group className="mb-3">
                                    <Form.Label>Position (Optional)</Form.Label>
                                    <Form.Select
                                      value={selectedPositionId}
                                      onChange={(e) =>
                                        setSelectedPositionId(
                                          parseInt(e.target.value),
                                        )
                                      }
                                    >
                                      <option value="0">Select Position</option>
                                      {POSITIONS.map((position) => (
                                        <option
                                          key={position.id}
                                          value={position.id}
                                        >
                                          {position.name} ({position.abbr})
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </Form.Group>
                                </Col>
                                <Col md={2} className="d-flex align-items-end">
                                  <Form.Group className="mb-3">
                                    <Button
                                      variant="primary"
                                      onClick={handleAddPlayer}
                                    >
                                      Add
                                    </Button>
                                  </Form.Group>
                                </Col>
                              </Row>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default PlayerPickerModal;
